import React, { useEffect } from "react";
import {
  FaCheckCircle,
  FaExclamationTriangle,
  FaInfoCircle,
  FaTimes,
  FaTimesCircle,
} from "react-icons/fa";

interface ToastNotification {
  id: string;
  type: "success" | "error" | "info" | "warning";
  title: string;
  message: string;
  duration?: number;
}

interface ToastNotificationProps {
  notification: ToastNotification;
  onClose: (id: string) => void;
}

const ToastNotification: React.FC<ToastNotificationProps> = ({
  notification,
  onClose,
}) => {
  const { id, type, title, message, duration = 4000 } = notification;

  useEffect(() => {
    const timer = setTimeout(() => {
      onClose(id);
    }, duration);

    return () => clearTimeout(timer);
  }, [id, duration, onClose]);

  const getConfig = () => {
    switch (type) {
      case "success":
        return {
          bgColor: "bg-green-600",
          Icon: FaCheckCircle,
          iconColor: "text-white",
          textColor: "text-white",
          progressColor: "bg-green-200",
        };
      case "error":
        return {
          bgColor: "bg-red-600",
          Icon: FaTimesCircle,
          iconColor: "text-white",
          textColor: "text-white",
          progressColor: "bg-red-200",
        };
      case "warning":
        return {
          bgColor: "bg-yellow-400",
          Icon: FaExclamationTriangle,
          iconColor: "text-black",
          textColor: "text-black",
          progressColor: "bg-yellow-200",
        };
      case "info":
        return {
          bgColor: "bg-blue-600",
          Icon: FaInfoCircle,
          iconColor: "text-white",
          textColor: "text-white",
          progressColor: "bg-blue-200",
        };
      default:
        return {
          bgColor: "bg-slate-600",
          Icon: FaInfoCircle,
          iconColor: "text-white",
          textColor: "text-white",
          progressColor: "bg-gray-200",
        };
    }
  };

  const config = getConfig();
  // derive an explicit color value so we can set it as a CSS variable
  const textColorValue = config.textColor?.includes("white")
    ? "#ffffff"
    : config.textColor?.includes("black")
    ? "#0f172a"
    : "#ffffff";

  return (
    <div
      className={`
  toast-notification pointer-events-auto relative overflow-hidden rounded-xl shadow-lg backdrop-blur-sm border border-white/20
      ${config.bgColor} p-4 mb-3 max-w-sm
      transform transition-all duration-500 ease-out
      animate-slide-in-right hover:scale-105
    `}
      style={{ ["--toast-text" as any]: textColorValue }}
      role="status"
      aria-live="polite"
      aria-atomic="true"
      // Re-enable interactions on the toast itself while container has pointer-events: none
      // so users can dismiss the toast.
      // tailwind class 'pointer-events-auto' ensures clicks work
    >
      {/* Removed background pattern overlay to avoid reducing text contrast */}

      <div className="relative z-10 flex items-start gap-3">
        {/* Icon */}
        <div className="flex-shrink-0 mt-0.5">
          <config.Icon className={`w-5 h-5 text-current`} />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <h4 className={`toast-title font-semibold text-sm mb-1 truncate`}>
            {title}
          </h4>
          <p
            className={`toast-message text-sm leading-relaxed text-opacity-90`}
          >
            {message}
          </p>
        </div>

        {/* Close button */}
        <button
          onClick={() => onClose(id)}
          className="toast-close flex-shrink-0 p-1 rounded-lg hover:bg-white/20 transition-colors duration-200"
          aria-label="Close notification"
        >
          <FaTimes className={`w-3 h-3 text-current`} />
        </button>
      </div>

      {/* Progress bar */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/20">
        <div
          className={`h-full ${config.progressColor} animate-progress`}
          style={{
            animation: `progress ${duration}ms linear forwards`,
          }}
        />
      </div>
    </div>
  );
};

interface ToastContainerProps {
  notifications: ToastNotification[];
  onClose: (id: string) => void;
}

const ToastContainer: React.FC<ToastContainerProps> = ({
  notifications,
  onClose,
}) => {
  return (
    <div className="fixed top-4 right-4 z-[9999] space-y-2 pointer-events-none">
      {notifications.map((notification) => (
        <ToastNotification
          key={notification.id}
          notification={notification}
          onClose={onClose}
        />
      ))}
    </div>
  );
};

export { ToastContainer, ToastNotification };
export default ToastContainer;
